/** @file Bakes `<base>@<dir>` from an animation with -L/-R targets swapped; the direction turns authored. */
import { z } from 'zod';
import type { Draft } from 'immer';
import type { AnimationDoc, Dir, DirectionEntry, DirectionsDoc, Doc, Id, Warning } from '@pose2d/format/types';
import { ids } from '@pose2d/format/ids';
import { names } from '@pose2d/format/names';
import { animationParts } from '@pose2d/format/doc/schemaParts/animation';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { currentOf } from '../_shared/currentOf';
import { animationDraft } from './animationDraft';
import { mirrorDeform } from './mirrorDeform';
import { mirrorTimelines } from './mirrorTimelines';
import { sameJson } from './sameJson';
import { sideCounterparts } from './sideCounterparts';
import { skipIkChildInherit } from './skipIkChildInherit';

type Input = { source: string; direction: Dir; id?: string; swapItems?: boolean; replace?: boolean };

/** The project's direction set after checking that it exists and contains `dir` (VALIDATION at `direction`). */
function checkedDirectionSet(draft: Draft<Doc>, dir: Dir): Draft<DirectionsDoc> {
  const set = draft.directions ?? throwError('VALIDATION', undefined, { path: 'direction', hint: 'the project has no direction set yet (direction.setCount)' });
  // The frozen set schema owns the layout rule: a set holding only `dir` parses exactly when its layout allows `dir`.
  const probe = { count: set.count, ...(set.layout4 === undefined ? {} : { layout4: set.layout4 }), entries: { [dir]: { mode: 'authored' } } };
  if (!animationParts.directions.safeParse(probe).success) {
    throwError('VALIDATION', undefined, { path: 'direction', hint: `${dir} is not part of this ${set.count}-direction set` });
  }
  return set;
}

/** The id of the baked animation: the existing one under `replace`, else the client id or a minted one. */
function targetId(name: string, input: Input, ctx: CommandContext): Id {
  const existing = ctx.index.byName.get('animation')?.get(name);
  if (existing !== undefined) {
    if (input.replace !== true) return throwError('NAME_TAKEN', { kind: 'animation', name }, { path: 'direction', hint: 'pass replace: true to overwrite it, or delete it first' });
    if (input.id !== undefined && input.id !== existing) return throwError('VALIDATION', undefined, { path: 'id', hint: `replace keeps the id ${existing}` });
    return existing;
  }
  const id = input.id ?? ctx.newId('anim');
  if (!ids.re('anim').test(id)) return throwError('VALIDATION', undefined, { path: 'id', hint: 'expected an anim_ id' });
  if (ctx.index.kindOf.has(id)) return throwError('ID_TAKEN', { id }, { path: 'id' });
  return id;
}

/** The INFO for deform keys of `attId` that could not be mirrored. */
function droppedWarning(draft: Draft<Doc>, srcId: Id, attId: Id): Warning {
  const name = draft.attachments[attId]?.name ?? attId;
  return { code: 'INFO', message: `deform keys of "${name}" were dropped: no mirrorVertexMap pairs it with its counterpart`, params: { attachment: name }, path: `animations.${srcId}.deform.${attId}` };
}

/** animation.mirrorAnimation: verbatim keys on swapped targets; `<base>@<direction>` becomes authored with flipX. */
export const command = defineCommand({
  name: 'animation.mirrorAnimation',
  description: 'Bake `<base>@<direction>` from animation `source`: keys copied verbatim onto the -L/-R counterparts (bones, slots, IK, attachment keys, draw order), bendPositive flipped, deform via mirrorVertexMap; socket items stay unless swapItems; the direction becomes authored with flipX; replace overwrites an existing target.',
  input: z
    .object({
      source: z.string().min(1),
      direction: prim.dir,
      id: z.string().exactOptional(),
      swapItems: z.boolean().exactOptional(),
      replace: z.boolean().exactOptional(),
    })
    .strict(),
  apply(draft, input, ctx) {
    const { id: srcId, anim: live } = animationDraft(draft, input.source, ctx);
    const src = currentOf(live);
    const name = `${names.splitAnimation(src.name).base}@${input.direction}`;
    if (name === src.name) throwError('VALIDATION', undefined, { path: 'direction', hint: `${src.name} already is the ${input.direction} animation` });
    const set = checkedDirectionSet(draft, input.direction);
    const id = targetId(name, input, ctx);
    const sides = sideCounterparts(draft, ctx.index, input.swapItems !== true);
    const { deform, dropped } = mirrorDeform(draft, src.deform, sides);
    const baked: AnimationDoc = { name, ...(src.durationFrames === undefined ? {} : { durationFrames: src.durationFrames }), ...mirrorTimelines(src, sides), deform };
    const entry: DirectionEntry = { mode: 'authored', flipX: true };
    for (const attId of dropped) ctx.warnings.push(droppedWarning(draft, srcId, attId));
    skipIkChildInherit(draft, baked, Object.keys(src.bones).map((bone) => [bone, sides.bone(bone)] as const), `animations.${srcId}`, ctx);
    ctx.created[name] = id;
    // replace onto a target that already holds this bake: no write, no stamp (W2-31, L-review m1).
    if (sameJson(currentOf(draft.animations)[id], baked) && sameJson(currentOf(set).entries[input.direction], entry)) return;
    draft.animations[id] = baked;
    set.entries[input.direction] = entry;
    draft.meta.updatedAt = ctx.now;
  },
});
